import React, { useState } from 'react';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { activitiesAPI } from '../../../services/api';
import { STORAGE_PATHS } from '../../../config/constants';
import LocationSelector from '../../common/Forms/LocationSelector';
import './EditActivityForm.css';

function EditActivityForm({ activity, onClose, onActivityUpdated }) {
    const [formData, setFormData] = useState({
        title: activity.title || '',
        description: activity.description || '',
        date: activity.date ? activity.date.split('T')[0] : '',
        country: activity.country || '',
        state: activity.state || '',
        location: activity.location || '',
        images: activity.images || []
    });
    
    const [imageFiles, setImageFiles] = useState([]);
    const [previewImages, setPreviewImages] = useState(activity.images || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLocationChange = (type, value) => {
        setFormData(prev => {
            const newState = { ...prev, [type]: value };
            if (type === 'country') {
                newState.state = '';
                newState.location = '';
            }
            if (type === 'state') {
                newState.location = '';
            }
            return newState;
        });
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            // Add new files to existing ones
            setImageFiles(prev => [...prev, ...files]);
            
            // Create preview URLs
            const newPreviews = files.map(file => URL.createObjectURL(file));
            setPreviewImages(prev => [...prev, ...newPreviews]);
        }
    };

    const handleRemoveImage = (index) => {
        // If the image is a new upload (has a file)
        if (index < imageFiles.length) {
            setImageFiles(prev => prev.filter((_, i) => i !== index));
        }
        
        // If it's from the existing images array
        const updatedImages = [...previewImages];
        updatedImages.splice(index, 1);
        setPreviewImages(updatedImages);
        
        // Update form data images array
        const updatedFormDataImages = [...formData.images];
        if (index < updatedFormDataImages.length) {
            updatedFormDataImages.splice(index, 1);
            setFormData(prev => ({
                ...prev,
                images: updatedFormDataImages
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setMessage('');

        try {
            // Start with existing images that haven't been removed
            let updatedImages = [...formData.images]; 
            
            // Upload new images
            if (imageFiles.length > 0) {
                setMessage('Uploading images...');
                const storage = getStorage();
                
                for (const file of imageFiles) {
                    try {
                        // Use the correct storage path constant
                        const imagePath = `${STORAGE_PATHS.ACTIVITY_IMAGES || 'activity-images'}/${Date.now()}_${file.name}`;
                        const imageRef = storageRef(storage, imagePath);
                        
                        // Upload the file
                        const uploadResult = await uploadBytes(imageRef, file);
                        
                        // Get download URL
                        const downloadURL = await getDownloadURL(uploadResult.ref);
                        updatedImages.push(downloadURL);
                    } catch (uploadError) {
                        setError(`Failed to upload image: ${file.name} - ${uploadError.message}`);
                        setLoading(false);
                        return;
                    }
                }
                
                setMessage('Images uploaded successfully');
            }
            
            // Create updated activity data
            const updatedActivity = {
                ...formData,
                images: updatedImages
            };
                        
            // Call API to update activity
            await activitiesAPI.updateActivity(activity.id, updatedActivity);
            
            setMessage('Activity updated successfully');
            setTimeout(() => {
                onActivityUpdated();
            }, 1000);
            
        } catch (error) {
            setError(error.message || 'Failed to update activity');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="edit-form-overlay">
            <div className="edit-form">
                <h2>Edit Activity</h2>
                
                {error && <div className="error-message">{error}</div>}
                {message && <div className="success-message">{message}</div>}
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Title *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Description *</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                            rows="4"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Date *</label>
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Location *</label>
                        <LocationSelector
                            countryCode={formData.country}
                            stateCode={formData.state}
                            city={formData.location}
                            onCountryChange={(value) => handleLocationChange('country', value)}
                            onStateChange={(value) => handleLocationChange('state', value)}
                            onCityChange={(value) => handleLocationChange('location', value)}
                            required={true}
                            className="location-selector-wrapper"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label className="form-label">Images (Up to 10)</label>
                        <input
                            type="file"
                            id="editActivityImagesInput"
                            accept="image/*"
                            onChange={handleFileChange}
                            multiple
                            className="form-file-input"
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="editActivityImagesInput" className="form-file-label">
                            <i className="fas fa-upload"></i>
                            {imageFiles.length > 0 ? `${imageFiles.length} file(s) chosen` : (previewImages.length > 0 ? 'Change Images' : 'Choose Images')}
                        </label>
                        
                        {previewImages.length > 0 && (
                            <div className="form-file-preview image-previews">
                                {previewImages.map((preview, index) => (
                                    <div key={index} className="preview-item">
                                        <img src={preview} alt={`Preview ${index + 1}`} />
                                        <button
                                            type="button"
                                            className="remove-image"
                                            onClick={() => handleRemoveImage(index)}
                                        >
                                            &times;
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <div className="form-actions">
                        <button type="submit" disabled={loading}>
                            {loading ? 'Updating...' : 'Update Activity'}
                        </button>
                        <button type="button" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditActivityForm; 